import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Voice-to-text for a single text field via the browser's built-in Web Speech
 * API (no external service, no cost). `onAppend` is called with each finalized
 * chunk of recognized speech so the caller can append it to the field.
 *
 * Returns `supported: false` on browsers without SpeechRecognition (e.g.
 * Firefox) so callers can hide the mic button entirely.
 */
export function useDictation(onAppend: (text: string) => void) {
  const [listening, setListening] = useState(false);
  // Live, not-yet-finalized words so the field can show text appearing as the
  // user speaks. Cleared as each phrase finalizes (and committed via onAppend).
  const [interim, setInterim] = useState('');
  const recRef = useRef<any>(null);
  // Keep the latest callback without re-creating the recognizer.
  const onAppendRef = useRef(onAppend);
  onAppendRef.current = onAppend;

  const supported =
    typeof window !== 'undefined' &&
    !!((window as any).SpeechRecognition || (window as any).webkitSpeechRecognition);

  const stop = useCallback(() => {
    try { recRef.current?.stop(); } catch { /* ignore */ }
    setListening(false);
    setInterim('');
  }, []);

  const start = useCallback(() => {
    if (!supported) return;
    const SR = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    const rec = new SR();
    rec.lang = navigator.language || 'en-US';
    rec.continuous = true;      // keep listening until toggled off
    rec.interimResults = true;  // stream partial words so the field types live
    rec.onresult = (e: any) => {
      let finalText = '';
      let interimText = '';
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const r = e.results[i];
        if (r.isFinal) finalText += r[0].transcript;
        else interimText += r[0].transcript;
      }
      const t = finalText.trim();
      if (t) onAppendRef.current(t);   // commit finalized phrase to the field
      setInterim(interimText);         // show whatever is still being recognized
    };
    rec.onend = () => { setListening(false); setInterim(''); };
    rec.onerror = () => { setListening(false); setInterim(''); };
    recRef.current = rec;
    try {
      rec.start();
      setListening(true);
    } catch { /* already started / mic blocked */ }
  }, [supported]);

  const toggle = useCallback(() => {
    if (listening) stop(); else start();
  }, [listening, start, stop]);

  // Stop recognition if the component unmounts mid-dictation.
  useEffect(() => () => { try { recRef.current?.stop(); } catch { /* ignore */ } }, []);

  return { supported, listening, interim, toggle, stop };
}

/** Append spoken text to an existing field value with a single separating space. */
export function appendSpoken(prev: string, addition: string): string {
  if (!prev) return addition;
  return /\s$/.test(prev) ? prev + addition : `${prev} ${addition}`;
}
